import { apiFetch, readErrorMessage, readJson } from '../api/client';
import type {
  MyAppsCatalogItem,
  MyAppsExternalApp,
  MyAppsState,
  MyAppsUpdateRequest
} from './myAppsTypes';

export const MY_APPS_STATE_URL = '/api/apps/my-apps';
export const CUSTOM_APPS_URL = '/api/apps/custom-apps';
export const CUSTOM_APPS_ADMIN_URL = `${CUSTOM_APPS_URL}/admin`;
export const CUSTOM_APP_ICON_PREFIX = `${CUSTOM_APPS_URL}/icons/`;

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function asExternalApp(raw: unknown): MyAppsExternalApp | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = asString(row.id || row.Id).trim();
  if (!id) return null;
  return {
    id,
    title: asString(row.title ?? row.Title, id),
    description: asString(row.description ?? row.Description),
    category: asString(row.category ?? row.Category, 'Utilities'),
    icon: asString(row.icon ?? row.Icon),
    path: asString(row.path ?? row.Path, `/apps/link/${encodeURIComponent(id)}`),
    url: asString(row.url ?? row.Url),
    kind: asString(row.kind ?? row.Kind, 'link')
  };
}

export function asMyAppsState(raw: unknown): MyAppsState {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const catalogRaw = row.catalog ?? row.Catalog;
  const externalRaw = row.externalApps ?? row.ExternalApps;
  return {
    catalog: Array.isArray(catalogRaw) ? (catalogRaw as MyAppsCatalogItem[]) : [],
    enabledAppIds: asStringList(row.enabledAppIds ?? row.EnabledAppIds),
    pinnedToolIds: asStringList(row.pinnedToolIds ?? row.PinnedToolIds),
    recentToolIds: asStringList(row.recentToolIds ?? row.RecentToolIds),
    externalApps: Array.isArray(externalRaw)
      ? externalRaw.flatMap((item) => {
          const app = asExternalApp(item);
          return app ? [app] : [];
        })
      : []
  };
}

export async function fetchMyAppsState(): Promise<MyAppsState> {
  const res = await apiFetch(MY_APPS_STATE_URL);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return asMyAppsState(await readJson<unknown>(res));
}

export async function updateMyAppsPreferences(
  request: MyAppsUpdateRequest
): Promise<MyAppsState> {
  const res = await apiFetch(MY_APPS_STATE_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return asMyAppsState(await readJson<unknown>(res));
}

export async function updateMyAppsEnabled(enabledAppIds: string[]): Promise<MyAppsState> {
  return updateMyAppsPreferences({ enabledAppIds });
}

export const CUSTOM_APP_CATEGORIES = [
  'Utilities',
  'Productivity',
  'IT Tools',
  'Analytics',
  'Microsoft 365'
] as const;

export type CustomAppCategory = (typeof CUSTOM_APP_CATEGORIES)[number];

export interface CustomAppItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  url: string;
  allowedRoles: string[];
  allowedGroupIds: string[];
  isActive: boolean;
  sortOrder: number;
  hasIconImage: boolean;
  iconImageUrl: string | null;
}

export interface CustomAppPage {
  items: CustomAppItem[];
  total: number;
  skip: number;
  take: number;
}

export interface CustomAppCategoryCount {
  name: string;
  count: number;
}

export interface CustomAppQuery {
  q?: string;
  category?: string;
  skip?: number;
  take?: number;
}

export interface CustomAppUpsertRequest {
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  url: string;
  allowedRoles: string[];
  allowedGroupIds: string[];
  isActive: boolean;
}

function toQueryString(query: CustomAppQuery): string {
  const parts: string[] = [];
  if (query.q) parts.push(`q=${encodeURIComponent(query.q)}`);
  if (query.category) parts.push(`category=${encodeURIComponent(query.category)}`);
  if (query.skip != null) parts.push(`skip=${encodeURIComponent(String(query.skip))}`);
  if (query.take != null) parts.push(`take=${encodeURIComponent(String(query.take))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

function asPage(raw: Partial<CustomAppPage>): CustomAppPage {
  return {
    items: raw.items ?? [],
    total: Number(raw.total ?? 0),
    skip: Number(raw.skip ?? 0),
    take: Number(raw.take ?? 0)
  };
}

export interface CustomAppsAdminApi {
  list(query?: CustomAppQuery): Promise<CustomAppPage>;
  listCategories(): Promise<CustomAppCategoryCount[]>;
  create(body: CustomAppUpsertRequest): Promise<CustomAppItem>;
  update(id: string, body: CustomAppUpsertRequest): Promise<CustomAppItem>;
  uploadIcon(id: string, file: File): Promise<CustomAppItem>;
  clearIcon(id: string): Promise<CustomAppItem>;
  deactivate(id: string): Promise<void>;
  /** Admin-gated icon bytes, so a host site can preview another site's icons. */
  iconUrl(item: CustomAppItem): string | null;
}

/**
 * Builds the admin client for one site. `base` lets a host site administer another
 * site's Custom Apps through its own cross-site admin route.
 */
export function createCustomAppsAdminApi(base: string = CUSTOM_APPS_ADMIN_URL): CustomAppsAdminApi {
  return {
    list: async (query: CustomAppQuery = {}) => {
      const res = await apiFetch(`${base}/items${toQueryString(query)}`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return asPage(await readJson<Partial<CustomAppPage>>(res));
    },

    listCategories: async () => {
      const res = await apiFetch(`${base}/categories`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return readJson<CustomAppCategoryCount[]>(res);
    },

    create: async (body: CustomAppUpsertRequest) => {
      const res = await apiFetch(`${base}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return readJson<CustomAppItem>(res);
    },

    update: async (id: string, body: CustomAppUpsertRequest) => {
      const res = await apiFetch(`${base}/items/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return readJson<CustomAppItem>(res);
    },

    uploadIcon: async (id: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      const res = await apiFetch(`${base}/items/${encodeURIComponent(id)}/icon`, {
        method: 'POST',
        body
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return readJson<CustomAppItem>(res);
    },

    clearIcon: async (id: string) => {
      const res = await apiFetch(`${base}/items/${encodeURIComponent(id)}/icon`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      return readJson<CustomAppItem>(res);
    },

    deactivate: async (id: string) => {
      const res = await apiFetch(`${base}/items/${encodeURIComponent(id)}/deactivate`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
    },

    iconUrl: (item: CustomAppItem) => {
      if (!item.hasIconImage) return null;
      // The API returns the owning site's public icon path (with a cache-busting query).
      // Keep that suffix but serve it through this admin base so cross-site pages work.
      const suffix = item.iconImageUrl?.startsWith(CUSTOM_APP_ICON_PREFIX)
        ? item.iconImageUrl.slice(CUSTOM_APP_ICON_PREFIX.length)
        : encodeURIComponent(item.slug);
      return `${base}/icons/${suffix}`;
    }
  };
}

export const customAppsAdminApi = createCustomAppsAdminApi();

export function listCustomAppsAdmin(query: CustomAppQuery = {}): Promise<CustomAppPage> {
  return customAppsAdminApi.list(query);
}

export function listCustomAppsAdminCategories(): Promise<CustomAppCategoryCount[]> {
  return customAppsAdminApi.listCategories();
}

export function createCustomApp(body: CustomAppUpsertRequest): Promise<CustomAppItem> {
  return customAppsAdminApi.create(body);
}

export function updateCustomApp(
  id: string,
  body: CustomAppUpsertRequest
): Promise<CustomAppItem> {
  return customAppsAdminApi.update(id, body);
}

export function uploadCustomAppIcon(id: string, file: File): Promise<CustomAppItem> {
  return customAppsAdminApi.uploadIcon(id, file);
}

export function clearCustomAppIcon(id: string): Promise<CustomAppItem> {
  return customAppsAdminApi.clearIcon(id);
}

export function deactivateCustomApp(id: string): Promise<void> {
  return customAppsAdminApi.deactivate(id);
}
